import { AddIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  Select,
  Text,
  useToast,
} from '@chakra-ui/react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useReducedMotion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FilterDrawer from '../../../components/filter-drawer/FilterDrawer';
import FilterStateIndicator from '../../../components/filter-state-indicator/FilterStateIndicator';
import PageLoader from '../../../components/layout/page-loader/PageLoader';
import NoItemsFound from '../../../components/no-items-found/NoItemsFound';
import Pagination from '../../../components/pagination/Pagination';
import ResourceGrid from '../../../components/resource-grid/ResourceGrid';
import ViewToggle from '../../../components/view-toggle/ViewToggle';
import { useBatchedPractices } from '../../../hooks/useBatchedPractices';
import { useBroadSpecialtyOptions } from '../../../hooks/useBroadSpecialtyOptions';
import { useCustomFields } from '../../../hooks/useCustomFields';
import { usePagination } from '../../../hooks/usePagination';
import { usePermissions } from '../../../hooks/usePermissions';
import { useResourceGridActions } from '../../../hooks/useResourceGridActions';
import { postOfficeHoursList } from '../../../services/practices/postOfficeHoursList';
import { getProvidersList } from '../../../services/providers/getProvidersList';
import {
  OfficeHour,
  OfficeHoursApiResponse,
  Provider,
  ProvidersResponse,
} from '../../../types';
import { analytics } from '../../../utils/analytics';
import { extractSpecialtyOptions } from '../../../utils/broadSpecialtyOptions';
import { getClientIdWithFallback } from '../../../utils/customFieldUtils';
import {
  applyExternalFilterForNonAdmin,
  isExternalMarket,
} from '../../../utils/externalEntity';
import {
  listItemVariants,
  staggerContainer,
} from '../../../utils/motion';
import { MotionBox } from '../../../components/motion/MotionComponents';
import { stateOptions } from '../../../utils/stateOptions';
import { providerFilterMappings } from './providerFilterMappings';
import { filterOptions } from './providerFilterOptions';
import ProviderItem from './ProviderItem';
import { createProviderColumns } from './ProvidersColumns';
interface GroupedOfficeHours {
  [practice_id: string]: OfficeHour[];
}

const FILTERS_STORAGE_KEY = 'providersFilters';

const ProviderList: React.FC = () => {
  const [viewMode, setViewMode] = useState<'list' | 'table'>('list');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const prefersReducedMotion = useReducedMotion();

  // Get user permissions
  const { canEditProviders, isAdmin, permissions, isReady } =
    usePermissions();

  // Track if component is mounted to prevent unnecessary filter updates
  const isMountedRef = useRef(false);

  // Load filters from localStorage on mount
  const [filters, setFilters] = useState<
    Record<string, string | string[]>
  >(() => {
    try {
      const saved = localStorage.getItem(FILTERS_STORAGE_KEY);
      const parsed = saved ? JSON.parse(saved) : {};
      // Merge with search query from URL if present
      const urlQuery = searchParams.get('q');
      if (urlQuery) {
        return { ...parsed, q: urlQuery };
      }
      return parsed;
    } catch {
      return {};
    }
  });
  const filtersRef = useRef<Record<string, string | string[]>>({});
  const [clearFiltersSignal, setClearFiltersSignal] = useState(false);
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get('q') || '',
  );

  type SortOption =
    | 'lastNameAsc'
    | 'lastNameDesc'
    | 'firstNameAsc'
    | 'firstNameDesc'
    | 'status';

  const deriveInitialSortOption = (
    f: Record<string, string | string[]>,
  ): SortOption => {
    const rawGroupByStatus = f.groupByStatus;
    const groupByStatus = Array.isArray(rawGroupByStatus)
      ? rawGroupByStatus[0]
      : rawGroupByStatus;

    if (groupByStatus === 'true') return 'status';

    const rawLastNameSort = f.lastNameSort;
    const rawSortOrderAsc = f.sortOrderAsc;

    const lastNameSort =
      (Array.isArray(rawLastNameSort)
        ? rawLastNameSort[0]
        : rawLastNameSort) ?? 'true';
    const sortOrderAsc =
      (Array.isArray(rawSortOrderAsc)
        ? rawSortOrderAsc[0]
        : rawSortOrderAsc) ?? 'true';

    const isLastName = lastNameSort !== 'false';
    const isAsc = sortOrderAsc !== 'false';

    if (isLastName && isAsc) return 'lastNameAsc';
    if (isLastName && !isAsc) return 'lastNameDesc';
    if (!isLastName && isAsc) return 'firstNameAsc';
    return 'firstNameDesc';
  };

  const [sortOption, setSortOption] = useState<SortOption>(
    deriveInitialSortOption(filters),
  );

  // Mark as mounted after initial render
  useEffect(() => {
    isMountedRef.current = true;
  }, []);

  // Custom fetch function that captures duplicateProviders
  // Use ref to access latest filters without recreating callback
  const filtersRefForFetch = useRef(filters);
  useEffect(() => {
    filtersRefForFetch.current = filters;
  }, [filters]);

  const fetchProvidersWithDuplicates = useCallback(
    async (
      startToken: string | null,
      filtersParam?: Record<string, string | string[] | undefined>,
    ) => {
      // Use filtersParam if provided, otherwise use ref
      const filtersToUse = filtersParam || filtersRefForFetch.current;

      const response = await getProvidersList(
        startToken,
        {
          ...applyExternalFilterForNonAdmin(filtersToUse, permissions.isAdmin),
          includeRelatedPractices: 'true',
        },
        10,
      );

      if (response.data?.duplicateProviders) {
        setDuplicateProviders(response.data.duplicateProviders);
      } else {
        setDuplicateProviders({});
      }
      return response;
    },
    [permissions.isAdmin],
  );

  const {
    items: providers,
    loading,
    currentPage,
    totalPages,
    totalCount,
    handlePageChange,
    setItems,
    refetch,
  } = usePagination<Provider, ProvidersResponse>(
    fetchProvidersWithDuplicates,
    10,
    {},
    filters,
    isReady,
  );

  const relatedPracticeIds = useMemo(
    () =>
      Array.from(
        new Set(
          providers.flatMap((provider) =>
            (provider.relatedPractices || []).map(
              (practice) => practice.id,
            ),
          ),
        ),
      ),
    [providers],
  );

  const { practicesMap: enrichedPracticesMap } = useBatchedPractices(
    relatedPracticeIds,
  );

  const toast = useToast();
  const [officeHoursLoading, setOfficeHoursLoading] = useState(false);
  const [officeHours, setOfficeHours] = useState<GroupedOfficeHours>(
    {},
  );
  const [duplicateProviders, setDuplicateProviders] = useState<
    Record<string, string>
  >({});

  const { error: providerBroadSpecialtyError } =
    useBroadSpecialtyOptions({ entityType: 'ProviderData' });

  // Fetch custom fields to get Specialty options
  const clientId = getClientIdWithFallback();
  const { customFields: providerCustomFields } = useCustomFields(
    clientId,
    'ProviderData',
  );

  // Extract Specialty options from custom fields
  const specialtyOptions = useMemo(
    () => extractSpecialtyOptions(providerCustomFields),
    [providerCustomFields],
  );

  // Get market options from localStorage
  const statesData = useMemo(
    () => JSON.parse(localStorage.getItem('statesData') || '[]'),
    [],
  );

  const visibleStatesData = useMemo(() => {
    if (permissions.isAdmin) {
      return statesData;
    }

    return statesData
      .map(
        (state: {
          markets: { id: string; name: string; external?: boolean }[];
        }) => ({
          ...state,
          markets: (state.markets || []).filter(
            (market) => !isExternalMarket(market),
          ),
        }),
      )
      .filter(
        (state: { markets: { id: string; name: string }[] }) =>
          state.markets.length > 0,
      );
  }, [statesData, permissions.isAdmin]);

  // Memoize filter options to prevent recreation on every render
  // This ensures FilterDrawer receives updated options when specialtyOptions loads
  const memoizedFilterOptions = useMemo(
    () =>
      filterOptions(
        visibleStatesData,
        specialtyOptions,
        providerCustomFields,
      ),
    [visibleStatesData, specialtyOptions, providerCustomFields],
  );

  const fetchedOfficeHoursRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (providerBroadSpecialtyError) {
      toast({
        title: 'Specialty filters limited',
        description:
          'We could not load Broad-specialty options. Retry in a bit or refresh the page.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
    }
  }, [providerBroadSpecialtyError, toast]);

  // Resource grid actions for standardized action menu
  const resourceGridContext = {
    scopeType: 'provider' as const,
    scopeId: 'all',
    activeFilters: filters,
    columnsMode: 'providers' as const,
  };
  const resourceGridActions = useResourceGridActions(
    resourceGridContext,
  );

  const handleApplyFilters = useCallback(
    (newFilters: Record<string, string[] | string>) => {
      try {
        // Start fresh - only include filters that are in newFilters
        // This ensures removed filters are properly cleared
        const formattedFilters: Record<string, string | string[]> =
          {};

        // Process all filters from newFilters (only selected filters are included)
        Object.entries(newFilters).forEach(([uiKey, value]) => {
          if (
            value &&
            value !== '' &&
            (Array.isArray(value) ? value.length > 0 : true)
          ) {
            const apiKey = providerFilterMappings[uiKey] || uiKey;
            formattedFilters[apiKey] = value;
          }
          // If value is empty, don't add it (filter was removed)
        });

        // Sort is controlled separately via the sort dropdown (not part of
        // the filter drawer's fields) - preserve it instead of dropping it.
        setFilters((prev) => {
          const preservedSortKeys: Record<string, string | string[]> =
            {};
          (
            ['lastNameSort', 'sortOrderAsc', 'groupByStatus'] as const
          ).forEach((key) => {
            if (prev[key] !== undefined) {
              preservedSortKeys[key] = prev[key];
            }
          });
          return { ...formattedFilters, ...preservedSortKeys };
        });
        filtersRef.current = newFilters;

        // Reset to first page when applying new filters
        if (handlePageChange) {
          handlePageChange(1);
        }
      } catch (error) {
        // Reset filters on error
        setFilters({});
        filtersRef.current = {};
      }
    },
    [handlePageChange],
  );

  const handleSortChange = useCallback((value: SortOption) => {
    setSortOption(value);

    if (value === 'status') {
      setFilters((prev) => ({
        ...prev,
        lastNameSort: 'true',
        sortOrderAsc: 'true',
        groupByStatus: 'true',
      }));
      return;
    }

    const lastNameSort =
      value === 'lastNameAsc' || value === 'lastNameDesc'
        ? 'true'
        : 'false';
    const sortOrderAsc =
      value === 'lastNameAsc' || value === 'firstNameAsc'
        ? 'true'
        : 'false';

    setFilters((prev) => ({
      ...prev,
      lastNameSort,
      sortOrderAsc,
      groupByStatus: 'false',
    }));
  }, []);

  // Get market options for FilterStateIndicator
  const marketOptions = useMemo(() => {
    return visibleStatesData
      .flatMap((state: { markets: { id: string; name: string }[] }) =>
        state.markets.map((market) => ({
          label: market.name,
          value: market.id,
        })),
      )
      .sort(
        (
          a: { label: string; value: string },
          b: { label: string; value: string },
        ) => a.label.localeCompare(b.label),
      );
  }, [visibleStatesData]);

  // Removed duplicate debounced refetch - usePagination already handles filter changes

  // Track last saved filters to prevent unnecessary localStorage writes
  const lastSavedFiltersRef = useRef<string>('');

  // Persist filters to localStorage whenever they change
  useEffect(() => {
    const filtersString = JSON.stringify(filters);

    // Only save if filters actually changed
    if (filtersString === lastSavedFiltersRef.current) {
      return;
    }

    lastSavedFiltersRef.current = filtersString;

    try {
      localStorage.setItem(FILTERS_STORAGE_KEY, filtersString);
    } catch (error) {
      // Silently handle localStorage errors
    }
  }, [filters]);

  const clearFilters = () => {
    setFilters({});
    filtersRef.current = {};
    setSearchQuery('');
    setSearchParams({});
    setClearFiltersSignal((prev) => !prev);
    // Clear from localStorage
    localStorage.removeItem(FILTERS_STORAGE_KEY);
    // usePagination will automatically refetch when filters change
  };

  // Sorting is controlled separately via dropdown and should not appear
  // in the FilterStateIndicator, so strip sort-only keys here.
  const filtersForIndicator = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(filters).filter(
          ([key]) =>
            key !== 'lastNameSort' &&
            key !== 'sortOrderAsc' &&
            key !== 'groupByStatus',
        ),
      ),
    [filters],
  );

  // Handle search query changes with debounce
  // Only update filters if component is mounted (not on initial load)
  useEffect(() => {
    // Skip on initial mount to prevent duplicate filter changes
    if (!isMountedRef.current) {
      return;
    }

    const timeoutId = setTimeout(() => {
      if (searchQuery.trim()) {
        setSearchParams({ q: searchQuery.trim() });
        setFilters((prev) => ({ ...prev, q: searchQuery.trim() }));
        analytics.trackEvent('search', 'provider', {
          queryLength: searchQuery.trim().length,
        });
      } else {
        setSearchParams({});
        setFilters((prev) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { q, ...rest } = prev;
          return rest;
        });
      }
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, setSearchParams]);

  const groupByPracticeId = useCallback(
    (response: OfficeHoursApiResponse): GroupedOfficeHours => {
      return response.office_hours.reduce(
        (acc: GroupedOfficeHours, curr: OfficeHour) => {
          const key = curr.practice_id;
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(curr);
          return acc;
        },
        {},
      );
    },
    [],
  );

  const fetchOfficeHours = useCallback(
    async (practiceIds: string[]) => {
      try {
        setOfficeHoursLoading(true);
        const data = await postOfficeHoursList(practiceIds);
        const groupedOfficeHours = groupByPracticeId(data.data);
        setOfficeHours(groupedOfficeHours);
        fetchedOfficeHoursRef.current = new Set([
          ...fetchedOfficeHoursRef.current,
          ...practiceIds,
        ]);
      } catch (error) {
        toast({
          title: 'Failed to fetch office hours',
          description: `Please try again`,
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setOfficeHoursLoading(false);
      }
    },
    [groupByPracticeId, toast],
  );

  // Track last state to prevent unnecessary updates
  const lastUpdateStateRef = useRef<{
    providerIds: string;
    officeHoursKey: string;
  }>({ providerIds: '', officeHoursKey: '' });

  const updateProvidersWithOfficeHours = useCallback(() => {
    // Create stable keys to detect changes
    const currentProviderIds = providers
      .map((p) => p.id)
      .sort()
      .join(',');
    const currentOfficeHoursKey = JSON.stringify(
      Object.keys(officeHours)
        .sort()
        .map((id) => ({
          id,
          count: officeHours[id]?.length || 0,
        })),
    );

    // Only update if providers changed OR office hours changed
    const stateChanged =
      currentProviderIds !== lastUpdateStateRef.current.providerIds ||
      currentOfficeHoursKey !==
        lastUpdateStateRef.current.officeHoursKey;

    if (!stateChanged && providers.length > 0) {
      return;
    }

    lastUpdateStateRef.current = {
      providerIds: currentProviderIds,
      officeHoursKey: currentOfficeHoursKey,
    };

    setItems((prevItems: Provider[]) => {
      // Preserve order by mapping in the same order
      const updated = prevItems.map((item) => {
        if (
          item.relatedPractices &&
          item.relatedPractices.length > 0
        ) {
          return {
            ...item,
            relatedPractices: item.relatedPractices.map(
              (practice) => ({
                ...practice,
                office_hours: officeHours[practice.id] || [],
              }),
            ),
          };
        } else {
          return item;
        }
      });

      return updated;
    });
  }, [setItems, officeHours, providers]);

  useEffect(() => {
    const practiceIds = providers.flatMap(
      (provider) =>
        provider.relatedPractices?.map((practice) => practice.id) ||
        [],
    );

    const newPracticeIds = practiceIds.filter(
      (id) => !fetchedOfficeHoursRef.current.has(id),
    );

    if (newPracticeIds.length > 0 && !officeHoursLoading) {
      fetchOfficeHours(newPracticeIds);
    }
  }, [providers, officeHoursLoading, fetchOfficeHours]);

  useEffect(() => {
    // Only update if we have providers
    if (providers.length > 0) {
      updateProvidersWithOfficeHours();
    }
  }, [officeHours, updateProvidersWithOfficeHours, providers]);

  return (
    <>
      <Helmet>
        <title>Providers | CHS | Resource Matrix — Denim Health</title>
      </Helmet>
      <MotionBox
        ml={`${viewMode === 'table' ? '2rem' : '11.25rem'}`}
        mr={`${viewMode === 'table' ? '2rem' : '11.25rem'}`}
        mb="3rem"
        mt="1.75rem"
        initial={
          prefersReducedMotion
            ? { opacity: 1, y: 0 }
            : { opacity: 0, y: 6 }
        }
        animate={{ opacity: 1, y: 0 }}
      >
        <Box>
          <Flex
            justifyContent={'space-between'}
            alignItems={'center'}
            mb="1rem"
          >
            <Text
              fontSize={'1.25rem'}
              fontWeight={'700'}
              lineHeight={'120%'}
              color="text.secondary"
            >
              Providers
            </Text>
            <Flex gap="0.75rem" alignItems="center">
              {(canEditProviders() || isAdmin()) && (
                <Button
                  colorScheme="blue"
                  background={'background.brandLight'}
                  leftIcon={<AddIcon />}
                  size="md"
                  px={6}
                  py={3}
                  mr={'0.75rem'}
                  onClick={() => navigate('/providers/create')}
                >
                  Add Resource
                </Button>
              )}
              <ViewToggle
                viewMode={viewMode}
                setViewMode={setViewMode}
              />
              <Select
                size="md"
                width="13rem"
                height="2.5rem"
                borderRadius="0.375rem"
                value={sortOption}
                onChange={(e) =>
                  handleSortChange(e.target.value as SortOption)
                }
              >
                <option value="lastNameAsc">Last name A–Z</option>
                <option value="lastNameDesc">Last name Z–A</option>
                <option value="firstNameAsc">First name A–Z</option>
                <option value="firstNameDesc">First name Z–A</option>
                <option value="status">Status (On PAC first)</option>
              </Select>
              <FilterDrawer
                filterTitle="Providers"
                filterOptions={memoizedFilterOptions}
                onApplyFilters={handleApplyFilters}
                clearFilters={clearFiltersSignal}
                initialFilters={(() => {
                  // Convert API keys back to UI keys for FilterDrawer
                  const uiFilters: Record<string, string | string[]> =
                    {};
                  Object.entries(filters).forEach(
                    ([apiKey, value]) => {
                      // Find UI key from mapping (reverse lookup)
                      const uiKey =
                        Object.keys(providerFilterMappings).find(
                          (key) =>
                            providerFilterMappings[key] === apiKey,
                        ) || apiKey;
                      uiFilters[uiKey] = value;
                    },
                  );
                  return uiFilters;
                })()}
              />
            </Flex>
          </Flex>

          {/* Filter State Indicator */}
          <FilterStateIndicator
            appliedFilters={filtersForIndicator}
            totalCount={totalCount || 0}
            filteredCount={providers.length}
            resourceType="providers"
            onClearFilters={clearFilters}
            marketOptions={marketOptions}
            stateOptions={stateOptions}
          />

          {loading ? (
            <PageLoader height="45vh" />
          ) : providers.length > 0 ? (
            viewMode === 'table' ? (
              <MotionBox
                initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <ResourceGrid<Provider>
                  data={providers}
                  columns={createProviderColumns(duplicateProviders)}
                  actionMenuContext={resourceGridContext}
                  actionMenuActions={resourceGridActions}
                />
              </MotionBox>
            ) : (
              <MotionBox
                display="flex"
                flexDirection="column"
                variants={
                  prefersReducedMotion ? undefined : staggerContainer
                }
                initial={prefersReducedMotion ? undefined : 'initial'}
                animate={prefersReducedMotion ? undefined : 'animate'}
                gap={'1.5rem'}
                mb={'2.31rem'}
              >
                {providers.map((provider, i) => (
                  <MotionBox
                    key={`${provider.id}-${i}`}
                    variants={
                      prefersReducedMotion
                        ? undefined
                        : listItemVariants
                    }
                  >
                    <ProviderItem
                      provider={provider}
                      officeHoursLoading={officeHoursLoading}
                      duplicateProviders={duplicateProviders}
                      enrichedPracticesMap={enrichedPracticesMap}
                    />
                  </MotionBox>
                ))}
              </MotionBox>
            )
          ) : (
            <NoItemsFound
              mainMessage="No Providers Found"
              detailMessage="We couldn't find anything matching your search criteria."
              refetch={refetch}
              removeFilters={
                Object.keys(filters).length > 0
                  ? clearFilters
                  : undefined
              }
            />
          )}
        </Box>

        {/* Helper text showing current page range */}
        {!loading && totalCount > 0 && (
          <Text
            fontSize="0.875rem"
            color="gray.600"
            mb="1rem"
            textAlign="center"
          >
            Showing {(currentPage - 1) * 10 + 1}–
            {Math.min(currentPage * 10, totalCount)} of {totalCount}
          </Text>
        )}

        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </MotionBox>
    </>
  );
};

export default ProviderList;
